// Pure formatting helpers for the /app/leads page. No React or antd imports, so
// they can be unit-tested directly. Rendering decisions from AlaudaAI/contexts
// PR #15 (design doc D6, execution T4).

import dayjs from "dayjs"

// NULL / empty placeholder. Plain hyphen, deliberately not an em dash.
export const DASH = "-"

// The four booked-slot enum values an ads lead can carry, mapped to friendly
// labels. Kept in sync with anti-spam's time_pref enum.
export const ADS_TIME_LABELS = {
  tomorrow_morning: "Tomorrow morning",
  tomorrow_afternoon: "Tomorrow afternoon",
  tomorrow_evening: "Tomorrow evening",
  early_next_week: "Early next week",
}

// dash renders null / undefined / empty as the placeholder, everything else
// unchanged.
export function dash(value) {
  if (value === null || value === undefined || value === "") return DASH
  return value
}

// formatTimeSlot is source-aware. Only an ads lead carries the booked-slot enum,
// so only source === "ads" maps the four enum values to friendly labels. An
// organic lead carries free text and is returned verbatim, even when that text
// happens to equal an ads enum value (a borrower may literally type
// "tomorrow_morning"); it must never be normalised into the ads label. Null or
// empty shows the placeholder. A widespread run of nulls means gate G6 was not
// fixed; that is a signal to escalate, not to paper over in the UI.
export function formatTimeSlot(value, source) {
  if (value === null || value === undefined || value === "") return DASH
  if (source === "ads" && Object.prototype.hasOwnProperty.call(ADS_TIME_LABELS, value)) {
    return ADS_TIME_LABELS[value]
  }
  return value
}

// sourceTag returns the antd Tag color and label for a lead source. ads and
// organic are visually distinct.
export function sourceTag(source) {
  if (source === "ads") return { color: "blue", label: "Ads" }
  if (source === "organic") return { color: "green", label: "Organic" }
  return { color: "default", label: dash(source) }
}

// formatType shows a capitalised lead type (purchase / refi), placeholder for
// null. NULL lead_type is meaningful and is never defaulted to a value (D1).
export function formatType(leadType) {
  if (leadType === null || leadType === undefined || leadType === "") return DASH
  return String(leadType).charAt(0).toUpperCase() + String(leadType).slice(1)
}

// formatQualifiedAt renders a timestamp (RFC3339 from the API) as a readable
// local date-time, placeholder for null.
export function formatQualifiedAt(occurredAt) {
  if (occurredAt === null || occurredAt === undefined || occurredAt === "") return DASH
  const d = dayjs(occurredAt)
  if (!d.isValid()) return DASH
  return d.format("MM/DD/YYYY h:mm A")
}

// leadSearchText builds the lowercased haystack for the free-text search across
// all seven columns, using the DISPLAYED values (the source label, the friendly
// time-slot label, the capitalised type) rather than raw source values, so a
// search for "organic" or "tomorrow morning" matches what the LO actually sees.
export function leadSearchText(row) {
  return [
    dash(row.Name),
    dash(row.Email),
    dash(row.Phone),
    sourceTag(row.Source).label,
    formatType(row.LeadType),
    formatQualifiedAt(row.OccurredAt),
    formatTimeSlot(row.TimeSlot, row.Source),
  ]
    .join(" ")
    .toLowerCase()
}
